// On-device store for UNSAVED drafts (notes SC4, generalised for Reports R3e — ruling D2: one IndexedDB database per
// feature, each with one store `drafts`, key `userId:id`). PHI on the device, so the rules are strict:
//   • IndexedDB only — never localStorage. Each feature's wrapper decides exactly which fields an entry holds.
//   • Wiped on EVERY sign-out: index.html _purgeLocalUserState() deletes every database in DRAFT_DBS and leaves the
//     `hud_drafts_wipe` flag (no user data), so a delete cut short by the sign-out navigation is finished here —
//     for ALL the draft databases — before anything is read, on the next load.
//   • Only the signed-in user's entries are ever read; other users' entries are deleted on first access.
//   • Unavailable (private mode / blocked / quota): every call resolves quietly (false / null / []), logged ❌ once per
//     store — the caller falls back to memory-only behaviour.
// Bundled into both app.bundle.js (notes, the reports sweep) and tiptap.bundle.js (the report editor): each copy has
// its own in-memory state; every operation opens its own transaction and the wipe is idempotent, so that is safe.
const WIPE_FLAG = 'hud_drafts_wipe';
export const DRAFT_DBS = ['hud_note_drafts', 'hud_report_drafts']; // every on-device draft database
const ST = 'drafts';
let _wipeP = null;

// A sign-out left the wipe flag: finish deleting EVERY draft database, then clear the flag (once per bundle copy).
function _finishWipe() {
  if (_wipeP) return _wipeP;
  let flagged = false;
  try { flagged = localStorage.getItem(WIPE_FLAG) === '1'; } catch (_) {}
  if (!flagged) return (_wipeP = Promise.resolve());
  _wipeP = DRAFT_DBS.reduce((p, name) => p.then(() => new Promise(res => {
    try {
      const r = indexedDB.deleteDatabase(name);
      r.onsuccess = r.onerror = r.onblocked = () => res();
    } catch (e) { res(); }
  })), Promise.resolve()).then(() => { try { localStorage.removeItem(WIPE_FLAG); } catch (_) {} });
  return _wipeP;
}

// → { available, put(userId, id, entry), get(userId, id), del(userId, id), all(userId) }
export function makeDraftStore({ db: DBN, label }) {
  let _dbp = null, _broken = false, _warned = false;
  function _fail(e) { _broken = true; _dbp = null; if (!_warned) { _warned = true; console.error('❌ ' + label + ' unavailable (memory only):', e); } }
  function _open() {
    if (_broken) return Promise.resolve(null);
    if (_dbp) return _dbp;
    _dbp = _finishWipe().then(() => new Promise(res => {
      try {
        if (!window.indexedDB) throw new Error('indexedDB not available');
        const r = indexedDB.open(DBN, 1);
        r.onupgradeneeded = () => { try { r.result.createObjectStore(ST); } catch (_) { /* exists */ } };
        r.onsuccess = () => { const db = r.result; db.onversionchange = () => { try { db.close(); } catch (_) {} _dbp = null; }; res(db); }; // never block a sign-out's delete
        r.onerror = () => { _fail(r.error); res(null); };
        r.onblocked = () => { _fail(new Error('indexedDB open blocked')); res(null); };
      } catch (e) { _fail(e); res(null); }
    }));
    return _dbp;
  }
  // Run `fn(store)` in one transaction; resolves with fn's collected value once the transaction has COMMITTED.
  async function _tx(mode, fn) {
    const db = await _open(); if (!db) return { ok: false };
    return new Promise(res => {
      let out;
      try {
        const tx = db.transaction(ST, mode);
        tx.oncomplete = () => res({ ok: true, value: out });
        tx.onerror = tx.onabort = () => { _fail(tx.error || new Error('indexedDB transaction failed')); res({ ok: false }); };
        out = fn(tx.objectStore(ST));
      } catch (e) { _fail(e); res({ ok: false }); }
    });
  }
  const _key = (userId, id) => String(userId) + ':' + String(id);
  return {
    available: () => !_broken,
    // Write/replace one entry (the caller builds it from its whitelist). → true once committed.
    put: async (userId, id, entry) => (await _tx('readwrite', st => { st.put(entry, _key(userId, id)); })).ok,
    get: async (userId, id) => {
      const box = {};
      const r = await _tx('readonly', st => { const q = st.get(_key(userId, id)); q.onsuccess = () => { box.v = q.result; }; return box; });
      const v = r.ok ? box.v : null;
      return (v && v.userId === String(userId)) ? v : null;
    },
    del: async (userId, id) => (await _tx('readwrite', st => { st.delete(_key(userId, id)); })).ok,
    // This user's entries. Any other user's entry is deleted on the way (the per-user boundary).
    all: async (userId) => {
      const mine = [];
      const r = await _tx('readwrite', st => {
        const c = st.openCursor();
        c.onsuccess = () => { const cur = c.result; if (!cur) return;
          if (cur.value && cur.value.userId === String(userId)) mine.push(cur.value); else cur.delete();
          cur.continue(); };
      });
      return r.ok ? mine : [];
    },
  };
}
